import { useState } from 'react';
import Button from '@ui/components/Button/Button';
import Icon from '@ui/components/Icon/Icon';
import useScimTokenQuery from '../../queries/useScimTokenQuery';
import useGenerateScimTokenMutation from '../../queries/useGenerateScimTokenMutation';
import ScimKeyAvailableModal from '../../modals/ScimKeyAvailableModal/ScimKeyAvailableModal';

import './ScimProvisioningCard.scss';

const MASKED_TOKEN = '****************';

const formatTokenDate = ( isoDate: string ) => new Intl.DateTimeFormat(
	'en-US', { month: 'short', day: '2-digit', year: 'numeric' }
).format( new Date( isoDate ) );

type Props = {
	workspaceID: number;
};

const ScimProvisioningCard = ( { workspaceID }: Props ) => {
	const [ plainToken, setPlainToken ] = useState<string | null>( null );
	const [ copiedUrl, setCopiedUrl ] = useState( false );
	const { data: scimToken, isLoading } = useScimTokenQuery( { workspaceID } );
	const { mutate: generateToken, isLoading: isGenerating } = useGenerateScimTokenMutation( {
		workspaceID,
		onSuccess: setPlainToken
	} );

	const handleCopyUrl = () => {
		if ( !scimToken ) return;
		navigator.clipboard.writeText( scimToken.scimUrl );
		setCopiedUrl( true );
		setTimeout( () => setCopiedUrl( false ), 2000 );
	};

	const handleGenerate = () => generateToken();
	const handleCloseModal = () => setPlainToken( null );

	return (
		<div className="ScimProvisioningCard">
			<div className="ScimProvisioningCard-header">
				<h2 className="ScimProvisioningCard-title">SCIM Provisioning</h2>
				<p className="ScimProvisioningCard-description">
					Connect your identity provider to automatically manage VeryBusy accounts when employees join,
					change roles, or leave. Copy your SCIM key and endpoint to your identity provider.{' '}
					{/* Temporal: "Coming soon!" en lugar de "How to set up SCIM →" */}
					{/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
					<a className="ScimProvisioningCard-setup-link" href="#">Coming soon!</a>
				</p>
			</div>

			{!isLoading && (
				<div className="ScimProvisioningCard-rows">
					<div className="ScimProvisioningCard-row">
						<span className="ScimProvisioningCard-row-label">SCIM Base Url</span>
						<div className="ScimProvisioningCard-row-content">
							<span className="ScimProvisioningCard-row-value">{scimToken?.scimUrl}</span>
							<button
								className={`ScimProvisioningCard-copy-btn${copiedUrl ? ' ScimProvisioningCard-copy-btn--copied' : ''}`}
								onClick={handleCopyUrl}
							>
								{copiedUrl ? 'Copied' : 'Copy'}
								{copiedUrl
									? <Icon icon="check" size={16} />
									: <Icon icon="content-copy" size={16} className="ScimProvisioningCard-copy-icon" />}
							</button>
						</div>
					</div>

					<div className="ScimProvisioningCard-row">
						<span className="ScimProvisioningCard-row-label">Bearer Token</span>
						<div className="ScimProvisioningCard-row-content">
							<span className="ScimProvisioningCard-row-value">{MASKED_TOKEN}</span>
							{scimToken?.active ? (
								<>
									<span className="ScimProvisioningCard-row-date">
										Generated {scimToken.createdAt ? formatTokenDate( scimToken.createdAt ) : ''}
									</span>
									<button
										className="ScimProvisioningCard-rotate-btn"
										onClick={handleGenerate}
										disabled={isGenerating}
									>
										Rotate
										<Icon icon="refresh" size={16} />
									</button>
								</>
							) : (
								<>
									<span className="ScimProvisioningCard-row-date">-</span>
									<Button size="xs" variant="filled" onClick={handleGenerate} loading={isGenerating}>
										Generate Token
									</Button>
								</>
							)}
						</div>
					</div>

					{scimToken?.active && (
						<div className="ScimProvisioningCard-warning">
							<Icon icon="warning" size={16} style={{ flexShrink: 0, marginTop: 2 }} />
							<span>After rotating, update the token in your IdP before the old is revoked.</span>
						</div>
					)}
				</div>
			)}

			{plainToken && (
				<ScimKeyAvailableModal
					token={plainToken}
					onClose={handleCloseModal}
				/>
			)}
		</div>
	);
};

export default ScimProvisioningCard;
